import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError, AuthFetchWithResponse } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { DynamicPriceType, FeedInPrice, NullablePowerPrice, ThreeSixtySavings } from '../types'
import powerPriceToModelTransformer from '../transformers/power-price-to-model.transformer'
import { PriceDto } from './dto/power-prices.dto'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { ThreeSixtySavingsDto } from './dto/three-sixty-savings.dto'
import ThreeSixtySavingsToModelTransformer from '../transformers/three-sixty-savings-to-model.transformer'
import { startOfHour } from '@e3dc-react/shell/Libs/date'

/**
 * function to fetch all power prices within a given time range
 * @param installationId
 * @param authFetch
 * @param from
 * @param to
 * @returns Api Result with power prices or an error
 */
export const fetchPowerPrices = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse,
  from?: Moment,
  to?: Moment,
  timePeriod?: string
): Promise<ApiResult<NullablePowerPrice[], AuthFetchError | Error>> => {
  const timePeriodToUse = timePeriod === 'day' || timePeriod === 'month' ? timePeriod : undefined

  const getFilter = (from?: Moment, to?: Moment): string | undefined => {
    const utcFrom = from?.startOf('hour').toISOString()
    const utcTo = to?.startOf('hour').toISOString()

    if (!utcFrom && !utcTo) return undefined
    if (utcFrom && !utcTo) return `validFrom>=${utcFrom}`
    if (!utcFrom && utcTo) return `validFrom<=${utcTo}`
    return `validFrom>=${utcFrom},validFrom<=${utcTo}`
  }

  try {
    const response = await authFetch<PriceDto[]>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `tariffs/${installationId}/gross-prices`,
      method: 'GET',
      payload: { filter: getFilter(from, to), timePeriod: timePeriodToUse },
    })

    if (response.status === StatusCodes.OK) {
      const data = powerPriceToModelTransformer(response.data)
      return new ApiResult<NullablePowerPrice[], AuthFetchError | Error>(data)
    }
    return new ApiResult<NullablePowerPrice[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<NullablePowerPrice[]>(error)
  }
}

/**
 * This function is responsible of fetching the data of dynamic power prices
 * for both cases (Battery or Wallbox) wether it's a farm or single storage
 */
export const fetchThreeSixtySavings = async (
  fetchFor: DynamicPriceType,
  storageId: string | undefined,
  storagesList: string[],
  isFarm: boolean,
  authFetch: AuthFetchWithResponse,
  shouldUseNewRoute: boolean,
  from?: Moment,
  to?: Moment,
  resolution?: string
): Promise<ApiResult<ThreeSixtySavings[], AuthFetchError | Error>> => {
  const oldEndpoint = fetchFor === DynamicPriceType.Wallbox ? process.env.REACT_APP_EMOBILITY_ENDPOINT : process.env.REACT_APP_INSTALLATIONS_ENDPOINT
  const newEndpoint = process.env.REACT_APP_INSTALLATIONS_ENDPOINT
  const endpointToUse = shouldUseNewRoute ? newEndpoint : oldEndpoint

  const oldPathForFarm = fetchFor === DynamicPriceType.Wallbox ? `e-mobility/bulk/charging/price-time-slot/savings` : 'tariffs/bulk/savings/storage'
  const newPathForFarm = fetchFor === DynamicPriceType.Wallbox ? `tariffs/bulk/savings/e-mobility` : 'tariffs/bulk/savings/storage'
  const farmPathToUse = shouldUseNewRoute ? newPathForFarm : oldPathForFarm

  const oldPathForSingleStorage =
    fetchFor === DynamicPriceType.Wallbox ? `e-mobility/${storageId}/charging/price-time-slot/savings` : `tariffs/${storageId}/savings/storage`
  const newPathForSingleStorage =
    fetchFor === DynamicPriceType.Wallbox ? `tariffs/${storageId}/savings/e-mobility` : `tariffs/${storageId}/savings/storage`
  const singleStoragePathForUse = shouldUseNewRoute ? newPathForSingleStorage : oldPathForSingleStorage

  const farmPayload = { sn: storagesList, from: from?.valueOf(), to: to?.valueOf(), resolution }
  const singleStoragePayload = { from: from?.valueOf(), to: to?.valueOf(), resolution }

  try {
    if (isFarm && storagesList.length > 0) {
      const response = await authFetch<ThreeSixtySavingsDto[]>({
        path: farmPathToUse,
        method: 'GET',
        payload: farmPayload,
        endpoint: endpointToUse,
      })

      if (response.status === StatusCodes.OK) {
        const data = ThreeSixtySavingsToModelTransformer(response.data)
        return new ApiResult<ThreeSixtySavings[], AuthFetchError | Error>(data)
      }
    } else if (!isFarm && storageId) {
      const response = await authFetch<ThreeSixtySavingsDto[]>({
        path: singleStoragePathForUse,
        method: 'GET',
        payload: singleStoragePayload,
        endpoint: endpointToUse,
      })

      if (response.status === StatusCodes.OK) {
        const data = ThreeSixtySavingsToModelTransformer(response.data)
        return new ApiResult<ThreeSixtySavings[], AuthFetchError | Error>(data)
      }
    }
    return new ApiResult<ThreeSixtySavings[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ThreeSixtySavings[]>(error)
  }
}

/**
 * function to fetch feed-in-prices within a given time range
 */
export const fetchFeedInPrices = async (
  installationId: string | undefined,
  from: Date,
  to: Date,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<FeedInPrice[], AuthFetchError | Error>> => {
  /* return error for missing parameters */
  if (!installationId || !from || !to) {
    return new ApiResult<FeedInPrice[], AuthFetchError | Error>(null, true)
  }

  const utcFrom = startOfHour(from).toISOString()
  const utcTo = startOfHour(to).toISOString()

  try {
    const response = await authFetch<FeedInPrice[]>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `tariffs/${installationId}/feed-in-prices`,
      method: 'GET',
      payload: { from: utcFrom, to: utcTo },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<FeedInPrice[], AuthFetchError | Error>(response.data)
    }
    return new ApiResult<FeedInPrice[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<FeedInPrice[]>(error)
  }
}
